/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { FormikHelpers, useFormik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { unstable_usePrompt } from 'react-router';

import { Button, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { Namespaces } from '../../../../i18n';
import { UserApiService } from '../../../../services/UserApiService';
import { RootState } from '../../../../store';
import { authSetUser, userLogout } from '../../../../store/auth/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { UnprocessableEntityPayload } from '../../../../types/api/UnprocessableEntityPayload';
import { IUser } from '../../../../types/user/IUser';
import { UpdateUserRequestBody } from '../../../../types/user/UpdateUserRequestBody';
import { unprocessibleEntityToObject } from '../../../../utils/unprocessibleEntityToObject';
import { useObjectState } from '../../../../utils/useObjectState';
import { AddressForm } from './components/AddressForm';
import { GeneralForm } from './components/GeneralForm';
import { validationSchema } from './PersonalForm.consts';
import { usePersonalFormStyles } from './PersonalForm.styles';
import { PersonalFormApiValidationErrors, PersonalFormValues, ResetApiErrorForField } from './PersonalForm.types';

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type PersonalFormProps = PropsFromRedux;

type State = {
  errorMessage?: string;
  isFetching: boolean;
  apiErrors: PersonalFormApiValidationErrors;
};

const getInitialValues = (user: IUser): PersonalFormValues => {
  const userAddress = user.address;
  return {
    firstName: user.firstName?.trim() || '',
    middleName: user.middleName?.trim(),
    lastName: user.lastName?.trim() || '',
    email: user.email?.trim() || '',
    language: user.language?.trim() || '',
    phoneCountryCode: user?.phoneCountryCode || 'at',
    phoneNationalNumber: user?.phoneNationalNumber || null,
    address: userAddress
      ? {
          line1: userAddress.line1.trim() || '',
          line2: userAddress.line2?.trim(),
          line3: userAddress.line3?.trim(),
          postalCode: userAddress.postalCode.trim() || '',
          city: userAddress.city.trim() || '',
          state: userAddress.state?.trim() || '',
          countryCode: userAddress.countryCode,
        }
      : {
          line1: '',
          line2: undefined,
          line3: undefined,
          postalCode: '',
          city: '',
          state: '',
          countryCode: 'at',
        },
  };
};

const PersonalFormComponent = memo<PersonalFormProps>(props => {
  const classes = usePersonalFormStyles();
  const { auth } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation(Namespaces.UserProfile);

  const [state, setState] = useObjectState<State>({
    isFetching: false,
    errorMessage: undefined,
    apiErrors: {},
  });

  const { apiErrors, errorMessage, isFetching } = state;

  const user = auth.user as IUser;
  const isLoading = auth.isFetching;

  const initialValues: PersonalFormValues = getInitialValues(user);

  const submit = async (
    values: PersonalFormValues,
    { setSubmitting, resetForm }: FormikHelpers<PersonalFormValues>,
  ) => {
    try {
      setState({ isFetching: true });
      const isEmailChanged = values.email !== user.email;
      const data: UpdateUserRequestBody = {
        firstName: values.firstName.trim(),
        middleName: values.middleName?.trim() || null,
        lastName: values.lastName.trim(),
        phoneCountryCode: values.phoneNationalNumber && values.phoneCountryCode ? values.phoneCountryCode : null,
        phoneNationalNumber: values.phoneNationalNumber?.trim().replace(/\D/g, '') || null,
        language: values.language.trim(),
        email: isEmailChanged ? values.email.trim() : undefined,
        address: {
          line1: values.address.line1.trim(),
          line2: values.address.line2?.trim() || null,
          line3: values.address.line3?.trim() || null,
          postalCode: values.address.postalCode.trim(),
          city: values.address.city.trim(),
          countryCode: values.address.countryCode,
          state: values.address.state?.trim() || null,
        },
      };
      const result = await UserApiService.updateUser(data);
      setState({ isFetching: false });
      if (isEmailChanged) {
        dispatch(userLogout());
      } else {
        setSubmitting(false);
        resetForm({ values: getInitialValues(result.data.payload) });
        dispatch(authSetUser(result.data.payload));
      }
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      const responseData = error.response?.data;
      if (!responseData) {
        setState({
          isFetching: false,
          errorMessage: undefined,
        });
        return;
      }
      if (responseData.message === 'unprocessable_entity') {
        const payload: UnprocessableEntityPayload<keyof PersonalFormApiValidationErrors> = responseData.payload;
        setState({
          apiErrors: unprocessibleEntityToObject(payload),
          isFetching: false,
        });
      } else if (responseData.message === 'duplicate_email') {
        setState({
          isFetching: false,
          apiErrors: { email: 'duplicate_email' },
        });
      } else {
        setState({
          isFetching: false,
          errorMessage: responseData.message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  const formProps = useFormik<PersonalFormValues>({
    onSubmit: submit,
    initialValues: initialValues,
    validationSchema: validationSchema,
  });

  unstable_usePrompt({
    when: formProps.dirty,
    message: t('profile.unsaved_form_question'),
  });

  return (
    <div className={classes.root}>
      {isLoading ? (
        <AlignCenter>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : initialValues ? (
        <form noValidate onSubmit={formProps.handleSubmit}>
          <GeneralForm {...formProps} apiErrors={apiErrors} resetApiError={resetApiError} isFetching={isFetching} />
          <AddressForm {...formProps} apiErrors={apiErrors} resetApiError={resetApiError} isFetching={isFetching} />
          {errorMessage && <div className={classes.errorBlock}>{t(`profile.error.${errorMessage}`)}</div>}
          <div className={classes.buttons}>
            <div className={classes.buttonWrap}>
              <Button
                variant="contained"
                type="submit"
                disabled={formProps.isValidating || isFetching || !formProps.dirty}
              >
                {t('profile.buttons.submit_form')}
              </Button>
            </div>
            <div className={classes.buttonWrap}>
              <Button
                onClick={() => formProps.resetForm()}
                variant="outlined"
                disabled={formProps.isValidating || isFetching || !formProps.dirty}
              >
                {t('profile.buttons.cancel_form')}
              </Button>
            </div>
          </div>
        </form>
      ) : null}
    </div>
  );
});

export const PersonalForm = connector(PersonalFormComponent);
